/**
 * How a special diet's child is named on screen.
 *
 * Three cases, and the label has to make which one it is obvious — a person
 * placing a meal order needs to know whether they are looking at a verified
 * roster student or a name somebody typed:
 *
 *   LINKED   "Avery Smith"                    a real roster user
 *   TYPED    "Avery Smith (not on roster)"    a name the office typed
 *   UNNAMED  "Student · Peanut, Tree Nut"     no name shared with us
 *
 * The unnamed label leads with the restriction because that is the only thing
 * distinguishing one unnamed child from another, and it is also the thing the
 * kitchen needs to get right.
 *
 * `reference` disambiguates two unnamed children with the SAME restriction at
 * the same school, which would otherwise render as two identical rows with no
 * way to tell which meal belongs to whom. It is a disambiguator, not an
 * identity: derived from the row id, stable, and meaningless outside Ordo.
 */

/** Enough of the row to label it; every field optional so partial selects work. */
export interface SpecialDietStudentLabelInput {
  id?: string | null
  /**
   * The FK. Authoritative for identity on its own, so a partial select that
   * omits the `student` relation cannot silently downgrade a LINKED row to
   * UNNAMED — which matters because `specialDietSupportsFamilyOrdering` gates
   * whether a family may order, and a label-shaped misread would decide it.
   */
  studentUserId?: string | null
  student?: { firstName?: string | null; lastName?: string | null; preferredName?: string | null } | null
  unlinkedStudentName?: string | null
  restrictedAllergens?: readonly string[] | null
  dietaryNeeds?: readonly string[] | null
}

export type SpecialDietStudentIdentity = 'LINKED' | 'TYPED' | 'UNNAMED'

/**
 * Which of the three cases this row is.
 *
 * Only LINKED may ever mint a household grant or be ordered by a family —
 * the other two have no identified user to bill or to authorize.
 */
export function specialDietStudentIdentity(input: SpecialDietStudentLabelInput): SpecialDietStudentIdentity {
  // Either signal is sufficient: callers that select the relation (labels) and
  // callers that select only the FK (gates) must agree on the answer.
  if (input.student || input.studentUserId) {
    return 'LINKED'
  }
  return input.unlinkedStudentName?.trim() ? 'TYPED' : 'UNNAMED'
}

/** True when a family (household or parent) may order this diet. */
export function specialDietSupportsFamilyOrdering(input: SpecialDietStudentLabelInput): boolean {
  return specialDietStudentIdentity(input) === 'LINKED'
}

/**
 * A short, stable disambiguator for an unnamed child, from the row id.
 *
 * Not an identifier anyone types or looks up — it exists so two unnamed
 * peanut-allergy children at one school are not two identical rows. Uppercased
 * because it sits inside a sentence-case label and must not read as a word.
 */
export function specialDietReference(id: string | null | undefined): string | null {
  const trimmed = id?.trim()
  if (!trimmed) {
    return null
  }
  return trimmed.slice(-4).toUpperCase()
}

/** The restrictions, humanised, for the unnamed label. */
function restrictionWords(input: SpecialDietStudentLabelInput): string[] {
  return [...(input.restrictedAllergens ?? []), ...(input.dietaryNeeds ?? [])]
    .map((slug) => slug.trim())
    .filter(Boolean)
    .map((slug) =>
      slug
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' '),
    )
}

/**
 * The display label. Never returns an empty string — a blank cell where a
 * child's identity belongs reads as a loading state, and someone will order
 * against the wrong row.
 */
export function specialDietStudentLabel(input: SpecialDietStudentLabelInput): string {
  const identity = specialDietStudentIdentity(input)

  if (identity === 'LINKED') {
    const student = input.student
    const name = [student?.preferredName?.trim() || student?.firstName?.trim(), student?.lastName?.trim()]
      .filter(Boolean)
      .join(' ')
      .trim()
    // A linked row with no name on the user is bad roster data, not an
    // unnamed diet — say so rather than silently borrowing the unnamed label.
    return name || 'Unnamed roster student'
  }

  if (identity === 'TYPED') {
    // The suffix is load-bearing: it tells the person ordering that Ordo has
    // not verified this against the roster, so a near-miss spelling is theirs
    // to catch.
    return `${input.unlinkedStudentName!.trim()} (not on roster)`
  }

  const restrictions = restrictionWords(input)
  const reference = specialDietReference(input.id)
  const base = restrictions.length > 0 ? `Student · ${restrictions.join(', ')}` : 'Student · restriction on file'
  return reference ? `${base} (${reference})` : base
}
